import React, { useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRetailerStore } from "../../store/useRetailerStore";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `${API_BASE}${url}`;
};

const urlToBase64 = async (url) => {
  try {
    const response = await fetch(url, { mode: "cors" });
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("Failed to convert image to base64:", err);
    return null;
  }
};

export default function ProductPDF({ product, retailer }) {
  const pdfRef = useRef(null);
  const retailerInfo = useRetailerStore((state) => state.retailerInfo);

  // 🔍 Debug Logs to check Zustand store vs Prop data
  console.log(" Zustand retailerInfo:", retailerInfo);
  console.log("📦 Passed retailer prop:", retailer);

  // Fallback to passed prop if Zustand is not populated yet
  const activeRetailer = retailerInfo || retailer;

  const generatePDF = async () => {
    if (!pdfRef.current) return;

    try {
      const images = pdfRef.current.querySelectorAll("img");
      const loadPromises = Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });
      await Promise.all(loadPromises);

      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      pdf.addImage(imgData, "JPEG", 0, 0, 297, 210);
      pdf.save(`${product?.sku || product?.name || "product"}.pdf`);
    } catch (error) {
      console.error("PDF generation failed:", error);
    }
  };

  const images = (product?.images || [])
    .map((image) => (typeof image === "string" ? image : image?.url))
    .filter(Boolean);

  const mainImage = images[0] || product?.image;

  // Derived from Zustand retailerInfo
  const logoUrl = activeRetailer?.logo_url ? getImageUrl(activeRetailer.logo_url) : null;
  const companyName = activeRetailer?.company_name || "MY COMPANY";

  console.log("🖼️ Resolved logoUrl:", logoUrl);
  console.log("🏢 Resolved companyName:", companyName);
  
  // Rotations for 4 distinct tile faces (0deg, 90deg, 180deg, 270deg)
  const rotations = [0, 90, 180, 270];

  return (
    <>
      <button
        type="button"
        onClick={generatePDF}
        className="py-2 px-3 bg-black hover:bg-gray-800 text-white rounded-md text-[11px] font-semibold transition-colors inline-flex items-center justify-center gap-1.5"
      >
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        Product PDF
      </button>

      {/* PDF TEMPLATE CONTAINER */}
      <div
        ref={pdfRef}
        style={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          width: "1123px",
          height: "794px",
          background: "#ffffff",
          color: "#111827",
          fontFamily: "Arial, sans-serif",
          padding: "30px 40px",
          boxSizing: "border-box",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            height: "50px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "1px solid #e5e7eb",
            paddingBottom: "10px",
          }}
        >
          <div>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={companyName}
                crossOrigin="anonymous"
                style={{
                  height: "38px",
                  maxWidth: "200px",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "900",
                    letterSpacing: "-0.5px",
                    lineHeight: "1",
                    color: "#000",
                  }}
                >
                  {companyName.toUpperCase()}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    letterSpacing: "3px",
                    color: "#9ca3af",
                    marginTop: "2px",
                  }}
                >
                  LOGO HERE
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              fontSize: "12px",
              color: "#9ca3af",
            }}
          >
            Powered by{" "}
            <span style={{ color: "#f97316", fontWeight: "600" }}>
              nitya.ai
            </span>
          </div>
        </div>

        {/* TITLE */}
        <div style={{ marginTop: "15px", marginBottom: "15px" }}>
          <h1
            style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: "700",
              color: "#111827",
            }}
          >
            {product?.name || product?.sku || "tile2"}
          </h1>
        </div>

        {/* CONTENT BODY */}
        <div style={{ display: "flex", gap: "30px" }}>
          {/* LEFT: MAIN BIG TILE PREVIEW */}
          <div
            style={{
              width: "560px",
              height: "560px",
              background: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {mainImage ? (
              <img
                src={getImageUrl(mainImage)}
                alt=""
                crossOrigin="anonymous"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <div style={{ color: "#9ca3af", fontSize: "14px" }}>
                No Image Available
              </div>
            )}
          </div>

          {/* RIGHT: ATTRIBUTES & 4 ROTATED TILE FACES GRID */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* ATTRIBUTES LIST */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: "20px",
                rowGap: "20px",
              }}
            >
              <Attribute
                label="SIZE"
                value={product?.size || product?.product_size || "600X600MM"}
              />
              <Attribute
                label="SURFACE"
                value={product?.surface_type || "GLOSSY"}
              />
              <Attribute
                label="RANDOM FACES"
                value={product?.random_faces || "4"}
              />
              <Attribute
                label="APPLICATION"
                value={
                  product?.application_type
                    ? String(product.application_type)
                        .replaceAll("_", " ")
                        .toUpperCase()
                    : "COUNTERTOP"
                }
              />
              <Attribute label="SKU" value={product?.sku || "-"} />
            </div>

            {/* 4 ROTATED FACES (2x2 GRID) */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
                width: "320px",
                height: "320px",
                marginTop: "20px",
              }}
            >
              {rotations.map((deg, idx) => (
                <div
                  key={idx}
                  style={{
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  {mainImage && (
                    <img
                      src={getImageUrl(mainImage)}
                      alt={`Face ${idx + 1}`}
                      crossOrigin="anonymous"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transform: `rotate(${deg}deg)`,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Attribute({ label, value }) {
  return (
    <div>
      <div
        style={{
          color: "#9ca3af",
          fontSize: "10px",
          fontWeight: "600",
          marginBottom: "2px",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "14px",
          fontWeight: "800",
          color: "#111827",
          textTransform: "uppercase",
          lineHeight: "1.2",
          wordBreak: "break-all",
        }}
      >
        {value}
      </div>
    </div>
  );
}